import * as Logger from "js-logger";
import { SceneConfig } from "slotworks/config/asset-config-schema";
import { TextConfig } from "slotworks/config/asset-config-schema";
import DOMLayer from "slotworks/graphics/layers/dom-layer";
import { SceneTransition } from "slotworks/graphics/layers/scene-transitions/scene-transition";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";

export default class Scene {

    public positions: Map<string, Rectangle> = new Map<string, Rectangle>();

    public portraitSpritePositions: Map<string, Rectangle> = new Map<string, Rectangle>();
    public landscapeSpritePositions: Map<string, Rectangle> = new Map<string, Rectangle>();
    public portraitTextConfigs: Map<string, TextConfig> = new Map<string, TextConfig>();
    public landscapeTextConfigs: Map<string, TextConfig> = new Map<string, TextConfig>();

    private transitions: Map<string, SceneTransition> = new Map<string, SceneTransition>();

    private activeTransition: SceneTransition;
    private transitionState: TransitionState;

    constructor(public name: string, public layer: string, public config?: SceneConfig) {
        if (this.config) {
            if (this.config.positions) {
                for (const position of this.config.positions) {
                    this.positions.set(position.id, new Rectangle(position.x, position.y, position.width, position.height));
                }
            }

            if (this.config.sprites) {
                this.landscapeSpritePositions = new Map<string, Rectangle>();
                for (const spriteConfig of this.config.sprites) {
                    const runtimeName = this.getSpriteRuntimeName(spriteConfig.id.split("/")[1]);

                    if (runtimeName !== "" && !this.landscapeSpritePositions.has(runtimeName)) {
                        this.landscapeSpritePositions.set(runtimeName, new Rectangle(spriteConfig.x, spriteConfig.y, spriteConfig.width, spriteConfig.height));
                    }
                }
            }

            if (this.config.portraitPositions) {
                for (const position of this.config.portraitPositions) {
                    const runtimeName = this.getSpriteRuntimeName(position.id);
                    const landscapePosition = this.landscapeSpritePositions.get(runtimeName);

                    if (landscapePosition) {
                        if (runtimeName !== "" && !this.portraitSpritePositions.has(runtimeName)) {
                            const portraitPosition = new Rectangle(position.x, position.y, position.width, position.height);

                            if (config.portraitBounds && config.bounds) {
                                const offset = new Rectangle(
                                    config.bounds.x - config.portraitBounds.x,
                                    config.bounds.y - config.portraitBounds.y,
                                    config.bounds.width / config.portraitBounds.width,
                                    config.bounds.height / config.portraitBounds.height
                                );
                                portraitPosition.x += offset.x;
                                portraitPosition.y += offset.y;

                                portraitPosition.x *= offset.width;
                                portraitPosition.y *= offset.height;
                                portraitPosition.width *= offset.width;
                                portraitPosition.height *= offset.height;
                            }
                            this.portraitSpritePositions.set(runtimeName, portraitPosition);

                        }
                    } else {
                        Logger.warn("Portrait position", position.id, "found but the sprite is missing: layerscene: ", this.layer + "_" + this.name);
                    }
                }
            }

            if (this.config.text) {
                for (const textConfig of this.config.text) {
                    this.landscapeTextConfigs.set(textConfig.id, textConfig);
                }
            }

            if (this.config.portraitText) {
                for (const textConfig of this.config.portraitText) {
                    const textId = textConfig.id;
                    if (config.portraitBounds && config.bounds) {
                        const offset = new Point(config.bounds.x - config.portraitBounds.x, config.bounds.y - config.portraitBounds.y);
                        textConfig.x += offset.x;
                        textConfig.y += offset.y;
                    }
                    this.portraitTextConfigs.set(textId, textConfig);
                }
            }
        }
    }

    public setTransitions(transitions: SceneTransition, scene: string) {
        this.transitions.set(scene, transitions);
    }

    public in(toScene: string = "*"): Contract<void> {
        let transition: SceneTransition;
        if (this.transitions.has(toScene)) {
            transition = this.transitions.get(toScene);
        } else if (this.transitions.has("*")) {
            transition = this.transitions.get("*");
        }

        if (transition) {
            this.transitionState = TransitionState.IN;
            this.activeTransition = transition;

            return new Contract<void>((resolve) => {
                transition.in().then(() => {
                    this.clearTransitionState();
                    resolve(null);
                });
            });
        }

        return Contract.empty();
    }

    public out(fromScene: string = "*"): Contract<void> {
        let transition: SceneTransition;
        if (this.transitions.has(fromScene)) {
            transition = this.transitions.get(fromScene);
        } else if (this.transitions.has("*")) {
            transition = this.transitions.get("*");
        }

        if (transition) {
            this.transitionState = TransitionState.OUT;
            this.activeTransition = transition;

            return new Contract<void>((resolve) => {
                transition.out().then(() => {
                    this.clearTransitionState();
                    resolve(null);
                });
            });
        }

        return Contract.empty();
    }

    public skipTransition() {
        if (this.transitionState) {
            if (this.transitionState === TransitionState.IN) {
                this.activeTransition.skipIn();
            } else {
                this.activeTransition.skipOut();
            }

            this.clearTransitionState();
        }
    }

    public hasCanvas(): boolean {
        return this.config !== null;
    }

    public hasDOMScene(): boolean {
        return DOMLayer.hasDOMScene(this.name, this.layer);
    }

    public clearDOM() {
        DOMLayer.setDOMScene(this.name, this.layer, false);
    }

    public showDOM() {
        if (this.hasDOMScene()) {
            DOMLayer.setDOMScene(this.name, this.layer, true);
        }
    }

    public destroyTransitions() {
        if (this.activeTransition) {
            this.activeTransition.destroy();
            this.activeTransition = null;
        }
    }

    public disableAutomaticOrientationPositioning(name: string) {
        const maps = [
            this.portraitSpritePositions,
            this.landscapeSpritePositions,
            this.portraitTextConfigs,
            this.landscapeTextConfigs
        ];

        for (const map of maps) {
            (map as any).forEach((value: any, key: string) => {
                if (key === name) {
                    map.delete(key);
                }
            });
        }
    }

    private clearTransitionState() {
        this.transitionState = null;
    }

    /**
     * TODO: This should be replaced with a uniform system for converting assetworks names to corresponding runtime names + meta data (to be reused by populators)
     */
    private getSpriteRuntimeName(assetworksName: string) {
        // Combine toggle meta into name where necessary
        let runtimeName = assetworksName.indexOf("tgl-") > -1 ? assetworksName.split("-")[1] + "_" + assetworksName.split("-")[2] : assetworksName;

        // Strip out any other meta data (sprite, btn, shape etc)
        if (runtimeName.indexOf("-") > -1) {
            const nameMetaData = runtimeName.split("-");

            // Ignore button states apart from up / unnamed
            if (nameMetaData[0] === "btn" && nameMetaData.length > 2 && nameMetaData[2] !== "up") {
                runtimeName = "";
            } else if ((nameMetaData[0] === "anim" || nameMetaData[0] === "spine") && nameMetaData.length > 2 && nameMetaData[2] !== "false" && nameMetaData[2] !== "true") {
                runtimeName = nameMetaData[2];
            } else {
                runtimeName = nameMetaData[1];
            }
        }

        return runtimeName;
    }
}

export enum TransitionState {
    IN = "in",
    OUT = "out"
}
